import { Layout } from "@/components/layout/Layout";
import { SEO } from "@/components/SEO";
import { SectionHeader } from "@/components/ui/SectionHeader";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import {
  Globe,
  Clock,
  TrendingDown,
  ShieldCheck,
  CheckCircle2,
  Phone,
  MessageCircle,
  ArrowRight,
  Building2,
  Briefcase,
  Scale,
  Headphones,
} from "lucide-react";

const internationalFaqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: [
    {
      "@type": "Question",
      name: "Can a South African bookkeeper handle my overseas business accounts?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Yes. Our SAICA-registered team is experienced with international accounting standards including IFRS. We work with clients in the UK, US, Australia, and across Europe, managing their South African entities or providing cost-effective outsourced bookkeeping.",
      },
    },
    {
      "@type": "Question",
      name: "What timezone do you operate in?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "We operate in South African Standard Time (SAST, UTC+2), which overlaps well with European, UK, and Middle Eastern business hours. We also accommodate US and Australian clients with flexible scheduling.",
      },
    },
    {
      "@type": "Question",
      name: "How much can I save by outsourcing bookkeeping to South Africa?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Businesses typically save 40-60% compared to hiring locally in the UK, US, or Australia. Our packages start from R2,750/month (approximately £120/US$150), offering significant cost advantages without compromising quality.",
      },
    },
    {
      "@type": "Question",
      name: "What accounting software do you support for international clients?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "We work with all major cloud-based platforms including Xero, QuickBooks Online, Sage, FreshBooks, and MYOB. Cloud software means seamless collaboration regardless of your location.",
      },
    },
    {
      "@type": "Question",
      name: "Is my financial data secure?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Absolutely. We follow strict data protection protocols compliant with POPIA (South Africa's data protection law, similar to GDPR). All data is transmitted via encrypted channels and stored securely on cloud platforms.",
      },
    },
    {
      "@type": "Question",
      name: "What bookkeeping services do you offer internationally?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "We provide monthly bookkeeping, bank reconciliations, accounts payable and receivable management, general ledger maintenance, management accounts, and financial reporting — all managed remotely via cloud-based platforms.",
      },
    },
  ],
};

const serviceSchema = {
  "@context": "https://schema.org",
  "@type": "Service",
  name: "International Outsourced Bookkeeping Services",
  provider: {
    "@type": "AccountingService",
    name: "Bookkeeping Services South Africa",
    url: "https://bookkeepingservices.co.za",
  },
  description:
    "Professional outsourced bookkeeping services from South Africa for international businesses. SAICA registered, IFRS compliant, cost-effective.",
  areaServed: [
    { "@type": "Country", name: "United Kingdom" },
    { "@type": "Country", name: "United States" },
    { "@type": "Country", name: "Australia" },
    { "@type": "Country", name: "Canada" },
    { "@type": "Country", name: "United Arab Emirates" },
    { "@type": "Country", name: "South Africa" },
  ],
  serviceType: [
    "Outsourced Bookkeeping",
    "Remote Bookkeeping",
    "Financial Reporting",
    "Management Accounts",
  ],
};

const benefits = [
  {
    icon: TrendingDown,
    title: "Save 40–60% on Costs",
    description:
      "Significantly lower your bookkeeping overheads compared to hiring locally in the UK, US, or Australia — without compromising quality.",
  },
  {
    icon: Clock,
    title: "Timezone-Friendly",
    description:
      "South Africa (UTC+2) overlaps with European and UK business hours perfectly, and we offer flexible scheduling for US and Australian clients.",
  },
  {
    icon: ShieldCheck,
    title: "SAICA Registered & IFRS Compliant",
    description:
      "Our team is registered with the South African Institute of Chartered Accountants and works to international financial reporting standards.",
  },
  {
    icon: Globe,
    title: "Cloud-Based Collaboration",
    description:
      "We work on Xero, QuickBooks, Sage, and other cloud platforms — real-time access to your books from anywhere in the world.",
  },
  {
    icon: Headphones,
    title: "Dedicated Account Manager",
    description:
      "Every international client gets a dedicated contact person for seamless communication and personalised service.",
  },
  {
    icon: Scale,
    title: "POPIA & Data Compliant",
    description:
      "Your financial data is protected under South Africa's POPIA legislation, equivalent to GDPR, with encrypted transmission and secure storage.",
  },
];

const idealClients = [
  {
    icon: Building2,
    title: "Foreign-Owned SA Entities",
    description:
      "International companies with South African subsidiaries needing reliable bookkeeping and financial record-keeping.",
  },
  {
    icon: Briefcase,
    title: "SMEs Looking to Outsource",
    description:
      "Small and medium businesses in the UK, US, or Australia seeking cost-effective, high-quality bookkeeping without hiring in-house.",
  },
  {
    icon: Globe,
    title: "Remote & Digital Businesses",
    description:
      "Online businesses, e-commerce stores, and digital agencies that operate globally and need a reliable bookkeeping partner.",
  },
];

const services = [
  "Monthly bookkeeping & reconciliations",
  "Accounts payable & receivable",
  "Bank reconciliations",
  "General ledger maintenance",
  "Management accounts & reporting",
  "Budgeting & cash flow forecasting",
];

export default function InternationalServices() {
  const whatsappNumber = "27732509394";
  const message = encodeURIComponent(
    "Hi! I'm an international client interested in your outsourced bookkeeping services."
  );

  return (
    <Layout>
      <SEO
        title="Outsource Bookkeeping to South Africa | International Services"
        description="Save 40-60% by outsourcing your bookkeeping to South Africa. SAICA registered, IFRS compliant, cloud-based. Serving UK, US, Australia & global businesses."
        canonical="/international-services"
        structuredData={[internationalFaqSchema, serviceSchema]}
      />

      {/* Hero */}
      <section className="relative py-20 lg:py-32 bg-primary overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: `radial-gradient(circle at 2px 2px, white 1px, transparent 0)`,
              backgroundSize: "32px 32px",
            }}
          />
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-primary-foreground/10 rounded-full px-4 py-2 mb-8 animate-fade-up">
              <Globe className="w-4 h-4 text-primary-foreground" />
              <span className="text-sm font-medium text-primary-foreground">
                Serving Businesses Worldwide
              </span>
            </div>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-primary-foreground mb-6 animate-fade-up leading-tight">
              Outsource Your Bookkeeping
              <span className="block">to South Africa</span>
            </h1>
            <p className="text-lg md:text-xl text-primary-foreground/80 mb-10 max-w-2xl mx-auto animate-fade-up leading-relaxed">
              Professional, SAICA-registered bookkeeping at a fraction of the cost.
              Trusted by businesses in the UK, US, Australia, and beyond.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-up">
              <Link to="/book-a-call">
                <Button
                  size="lg"
                  className="bg-primary-foreground text-primary hover:bg-primary-foreground/90 gap-2 text-base px-8 h-14"
                >
                  <Phone className="w-5 h-5" />
                  Book a Free Consultation
                </Button>
              </Link>
              <a
                href={`https://wa.me/${whatsappNumber}?text=${message}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button
                  size="lg"
                  variant="outline"
                  className="border-2 border-primary-foreground text-primary-foreground bg-transparent hover:bg-primary-foreground hover:text-primary gap-2 text-base px-8 h-14"
                >
                  <MessageCircle className="w-5 h-5" />
                  WhatsApp Us
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Why Outsource to SA */}
      <section className="py-20 lg:py-28 section-white">
        <div className="container mx-auto px-4">
          <SectionHeader
            label="Why South Africa"
            title="The Smart Choice for Outsourced Bookkeeping"
            description="South Africa offers a unique combination of skilled professionals, cost efficiency, and timezone compatibility that makes it ideal for international bookkeeping."
          />
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {benefits.map((item) => (
              <div key={item.title} className="card-elevated p-8">
                <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center mb-5">
                  <item.icon className="w-7 h-7 text-primary" />
                </div>
                <h3 className="font-heading text-lg font-semibold text-foreground mb-2">
                  {item.title}
                </h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Who We Serve */}
      <section className="py-20 lg:py-28 bg-muted/30">
        <div className="container mx-auto px-4">
          <SectionHeader
            label="Who We Serve"
            title="Ideal for International Businesses"
            description="Whether you have a South African subsidiary or simply want to outsource your bookkeeping, we've got you covered."
          />
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {idealClients.map((client) => (
              <div key={client.title} className="card-elevated p-8 text-center">
                <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-5">
                  <client.icon className="w-8 h-8 text-primary" />
                </div>
                <h3 className="font-heading text-lg font-semibold text-foreground mb-3">
                  {client.title}
                </h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {client.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services List */}
      <section className="py-20 lg:py-28 section-white">
        <div className="container mx-auto px-4">
          <SectionHeader
            label="Our Services"
            title="Remote Bookkeeping Services"
            description="Professional bookkeeping managed remotely from South Africa to keep your financial records accurate and up to date."
          />
          <div className="max-w-3xl mx-auto">
            <div className="grid sm:grid-cols-2 gap-4">
              {services.map((service) => (
                <div
                  key={service}
                  className="flex items-center gap-3 p-4 rounded-xl bg-muted/50"
                >
                  <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0" />
                  <span className="text-sm text-foreground font-medium">{service}</span>
                </div>
              ))}
            </div>
            <div className="text-center mt-10">
              <Link to="/services">
                <Button variant="outline" className="btn-outline-primary gap-2">
                  View All Services
                  <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 lg:py-28 bg-muted/30">
        <div className="container mx-auto px-4">
          <SectionHeader
            label="FAQs"
            title="Common Questions from International Clients"
            description="Everything you need to know about outsourcing your bookkeeping to South Africa."
          />
          <div className="max-w-3xl mx-auto space-y-6">
            {internationalFaqSchema.mainEntity.map((faq, i) => (
              <details
                key={i}
                className="group card-elevated overflow-hidden"
              >
                <summary className="flex items-center justify-between p-6 cursor-pointer list-none font-heading font-semibold text-foreground hover:text-primary transition-colors">
                  {faq.name}
                  <ArrowRight className="w-5 h-5 text-muted-foreground group-open:rotate-90 transition-transform" />
                </summary>
                <div className="px-6 pb-6 text-sm text-muted-foreground leading-relaxed">
                  {faq.acceptedAnswer.text}
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 lg:py-28 bg-primary relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: `radial-gradient(circle at 2px 2px, white 1px, transparent 0)`,
              backgroundSize: "32px 32px",
            }}
          />
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-primary-foreground mb-6">
              Ready to Save on Your Bookkeeping?
            </h2>
            <p className="text-xl text-primary-foreground/80 mb-10">
              Get a free consultation to see how much you could save by
              outsourcing your bookkeeping to our South African team.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/book-a-call">
                <Button
                  size="lg"
                  className="bg-primary-foreground text-primary hover:bg-primary-foreground/90 gap-2 text-base px-8 h-14"
                >
                  <Phone className="w-5 h-5" />
                  Book a Free Consultation
                </Button>
              </Link>
              <a
                href={`https://wa.me/${whatsappNumber}?text=${message}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button
                  size="lg"
                  variant="outline"
                  className="border-2 border-primary-foreground text-primary-foreground bg-transparent hover:bg-primary-foreground hover:text-primary gap-2 text-base px-8 h-14"
                >
                  <MessageCircle className="w-5 h-5" />
                  WhatsApp Us
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}
